import React, {forwardRef, ReactNode, Ref, useCallback, useMemo} from "react";
import {MuiRFF, useForm} from "../../form";
import {TextField, TextFieldProps} from "@mui/material";
import InputMask, {Props} from "react-input-mask";
import {FieldProps} from "react-final-form";


type MuiPhoneTextFieldProps = TextFieldProps & {
    form: false;
}

type FormPhoneTextFieldProps = MuiRFF.TextFieldProps & {
    form: true;
}

type PhoneTextFieldTypes = "MuiRFF" | "Mui";


export type PhoneTextFieldProps<T extends PhoneTextFieldTypes = "Mui"> =
    T extends "MuiRFF"
        ? FormPhoneTextFieldProps
        : T extends "Mui"
            ? MuiPhoneTextFieldProps
            : never;

const _PhoneTextfield = <T extends PhoneTextFieldTypes = "Mui">(props: PhoneTextFieldProps<T>, ref: Ref<HTMLInputElement>) => {
    const {form} = useForm() ?? {};
    const {onBlur, onChange, onKeyUp} = props as unknown as PhoneTextFieldProps;
    const inputMaskProps = useMemo<Omit<Props, 'mask'>>(() => ({
        ...props,
        form: undefined,
        onChange: undefined,
        onBlur: undefined,
    } as unknown as Props), [props])

    /**
     * Custom error function that only shows error when form has been submitted and not changed
     */
    const customShowErrorFunction = useCallback<MuiRFF.ShowErrorFunc>(({meta: {error, submitFailed}}) => {
        return (submitFailed && error);
    }, [])

    /**
     * Formats the value of the phone input.
     */
    const formatValue = useCallback<Required<FieldProps<any, any>>['format']>((val) => {
        return (val?.trim() ?? '')
            .replace(/[()_-]/g, '')
            // .replace(/\+1/g, '')
            .replaceAll(' ', '')
    }, [])

    return (
        <>
            {
                props.form &&
                // @ts-ignore
                <InputMask
                    {...inputMaskProps}
                    value={(form?.getFieldState(props.name)?.value ?? "")}
                    onChange={(e) => form?.change(e.target.name, e.target.value)}
                    maskChar={'_'}
                    mask={"+9 (999) 999-9999"}
                >
                    {/*@ts-ignore*/}
                    {((inputProps: TextFieldProps) =>
                            <MuiRFF.TextField
                                name={props.name}
                                variant={'outlined'}
                                showError={customShowErrorFunction}
                                fieldProps={{formatOnBlur: true, format: formatValue}}
                                {...inputProps}
                                ref={ref}
                                type={'tel'}
                            />
                    ) as unknown as ReactNode}
                </InputMask>
            }
            {
                !props.form &&
                // @ts-ignore
                <InputMask
                    {...inputMaskProps}
                    value={props.value as string}
                    onChange={onChange}
                    onBlur={(e) => onBlur?.({
                        ...e,
                        target: {
                            ...e.target,
                            value: !e.target.value
                                ? e.target.value
                                : formatValue(e.target.value.toString(), '')
                        }
                    })}
                    onKeyUp={(e) => onKeyUp?.({
                        ...e,
                        target: {
                            ...e.target,
                            value: !(e.target as any).value ? (e.target as any).value : formatValue((e.target as any).value.toString(), '')
                        } as any
                    })}
                    maskChar={'_'}
                    mask={"+9 (999) 999-9999"}
                >
                    {/*@ts-ignore*/}
                    {((inputProps: TextFieldProps) =>
                            <TextField
                                variant={'outlined'}
                                type={props.type}
                                {...inputProps}
                                ref={ref}
                            />
                    ) as unknown as ReactNode}
                </InputMask>
            }
        </>
    )
}

const PhoneTextfield = forwardRef(_PhoneTextfield)

export default PhoneTextfield;
